import { Controller } from "@hotwired/stimulus";

/**
 * google-map
 *
 * Contrôleur Stimulus pour afficher une carte Google Maps et Street View.
 * Écoute l'événement remote:update:success pour mettre à jour les coordonnées.
 *
 * Usage:
 *   <div data-controller="google-map"
 *        data-google-map-latitude-value="48.8566"
 *        data-google-map-longitude-value="2.3522"
 *        data-google-map-channel-value="listing_details">
 *     <div data-google-map-target="map" style="height: 200px;"></div>
 *     <div data-google-map-target="streetView" style="height: 200px;"></div>
 *   </div>
 */
export default class extends Controller {
  static targets = ["map", "streetView"];
  static values = {
    latitude: { type: Number, default: 0 },
    longitude: { type: Number, default: 0 },
    coordinatesPath: { type: String, default: "situation_details" },
    channel: { type: String, default: "listing_details" }
  };

  connect() {
    this.map = null;
    this.marker = null;
    this.panorama = null;

    // Écoute sur le document car l'événement bulle vers le haut, pas vers le bas
    this.onRemoteSuccess = (e) => this.handleRemoteSuccess(e);
    document.addEventListener("remote:update:success", this.onRemoteSuccess);

    if (this.hasValidCoordinates()) {
      this.initializeMap();
    }
  }

  disconnect() {
    document.removeEventListener("remote:update:success", this.onRemoteSuccess);
    this.map = null;
    this.marker = null;
    this.panorama = null;
  }

  handleRemoteSuccess(event) {
    // Filtrer par channel si défini
    const eventChannel = event?.detail?.channel;
    if (this.channelValue && eventChannel !== this.channelValue) return;

    const json = event?.detail?.json;
    if (!json) return;

    const situationData = this.lookup(json, this.coordinatesPathValue);
    if (!situationData) {
      this.hideMap();
      return;
    }

    const lat = parseFloat(situationData.latitude);
    const lng = parseFloat(situationData.longitude);

    if (isNaN(lat) || isNaN(lng) || (lat === 0 && lng === 0)) {
      this.hideMap();
      return;
    }

    this.latitudeValue = lat;
    this.longitudeValue = lng;
    this.showMap();
    this.updateMap();
  }

  hasValidCoordinates() {
    return this.latitudeValue !== 0 || this.longitudeValue !== 0;
  }

  hideMap() {
    this.element.style.display = "none";
  }

  showMap() {
    this.element.style.display = "";
  }

  initializeMap() {
    if (!this.hasMapTarget) return;
    if (!this.isGoogleMapsLoaded()) {
      // Réessayer après un délai si Google Maps n'est pas encore chargé
      setTimeout(() => this.initializeMap(), 500);
      return;
    }

    const center = new google.maps.LatLng(this.latitudeValue, this.longitudeValue);

    this.map = new google.maps.Map(this.mapTarget, {
      center: center,
      zoom: 13,
      panControl: false,
      zoomControl: true,
      mapTypeControl: true,
      scaleControl: false,
      overviewMapControl: false,
      rotateControl: false
    });

    this.marker = new google.maps.Marker({
      position: center,
      map: this.map
    });

    if (this.hasStreetViewTarget) {
      this.initializeStreetView();
    }
  }

  initializeStreetView() {
    if (!this.hasStreetViewTarget) return;

    this.panorama = new google.maps.StreetViewPanorama(this.streetViewTarget, {
      linksControl: false,
      panControl: false,
      fullscreenControl: false,
      zoomControl: false,
      addressControl: false
    });

    const sv = new google.maps.StreetViewService();
    sv.getPanorama(
      { location: { lat: this.latitudeValue, lng: this.longitudeValue }, radius: 100 },
      (data, status) => {
        if (status === "OK" && data?.location) {
          this.panorama.setPano(data.location.pano);
          this.panorama.setPov({ heading: 270, pitch: 0 });
          this.panorama.setVisible(true);
          this.streetViewTarget.style.display = "";
        } else {
          // Cacher Street View si pas disponible
          this.streetViewTarget.style.display = "none";
        }
      }
    );
  }

  updateMap() {
    if (!this.isGoogleMapsLoaded()) {
      this.initializeMap();
      return;
    }

    const center = new google.maps.LatLng(this.latitudeValue, this.longitudeValue);

    if (this.map) {
      this.map.setCenter(center);
      if (this.marker) {
        this.marker.setPosition(center);
      }
    } else {
      this.initializeMap();
      return;
    }

    if (this.hasStreetViewTarget) {
      this.updateStreetView();
    }
  }

  updateStreetView() {
    if (!this.panorama) {
      this.initializeStreetView();
      return;
    }

    const sv = new google.maps.StreetViewService();
    sv.getPanorama(
      { location: { lat: this.latitudeValue, lng: this.longitudeValue }, radius: 100 },
      (data, status) => {
        if (status === "OK" && data?.location) {
          this.panorama.setPano(data.location.pano);
          this.panorama.setPov({ heading: 270, pitch: 0 });
          this.panorama.setVisible(true);
          this.streetViewTarget.style.display = "";
        } else {
          this.streetViewTarget.style.display = "none";
        }
      }
    );
  }

  isGoogleMapsLoaded() {
    return typeof google !== "undefined" && google.maps && google.maps.Map;
  }

  /**
   * Récupère une valeur imbriquée dans un objet via un chemin en notation pointée.
   * @param {Object} obj - L'objet source
   * @param {string} path - Le chemin (ex: "situation_details.latitude")
   * @returns {*} La valeur trouvée ou undefined
   */
  lookup(obj, path) {
    if (!obj || !path) return undefined;
    const parts = path.split(".");
    let current = obj;
    for (const part of parts) {
      if (current == null) return undefined;
      current = current[part];
    }
    return current;
  }
}
